// SAM-199 second pass — a persistent "what is being booked" rail, visible
// from Guests & Scan onward, so the operator can see what/where/when/who
// (and, for a priced booking, the per-unit prices the server will confirm)
// without scrolling back up to Access. Read-only: it never writes to
// InvitationContext, only reads from it.
//
// New component: Tailwind + tokens.css only (SAM-175 freeze).

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Calendar, Clock, MapPin, Users, Ticket, Home, CalendarRange } from 'lucide-react';
import { useInvitationContext } from '../context/InvitationContext';
import { isDayService } from './serviceBooking';
import { useBookingService } from './useBookingService';
import { isPreviewOk, previewBookingPrice } from './pricingPreview';
import { labelOf } from './bookingService';
import { useT } from '../../../i18n/admin';

const Row = ({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) => (
  <div className="flex items-start gap-2 py-1.5">
    <span style={{ color: 'var(--ink-400)', marginTop: 1 }}>{icon}</span>
    <div className="min-w-0">
      <div className="text-[11px]" style={{ color: 'var(--ink-400)' }}>{label}</div>
      <div className="text-[13px] font-medium truncate" style={{ color: 'var(--ink-900)' }}>{value}</div>
    </div>
  </div>
);

export const BookingSummaryRail = () => {
  const { invitation } = useInvitationContext();
  const { t, lang, fmt } = useT();

  const destinationLabel = invitation?.location?.estate_id?.[0]?.label;
  const accessTypeLabel = invitation?.accessType?.[0]?.label;
  const adults = invitation?.guests?.companions?.adults ?? 0;
  const childrens = invitation?.guests?.companions?.childrens ?? 0;
  const guestCount = adults + childrens;

  const product = invitation?.product?.[0];
  const serviceBooking = invitation?.serviceBooking;
  // SAM-624 — the description-endpoint version of the service (rate groups the viewer can buy,
  // seasons, unit config); the raw list row until it arrives.
  const { service: detail } = useBookingService(invitation?.selectedService);
  const service = detail ?? invitation?.selectedService;

  // Display-only estimate mirroring pricing.py (see pricingPreview.ts); never sent anywhere.
  const preview = useMemo(() => {
    if (!detail || !serviceBooking) return null;
    const quantities: Record<string, number> = {};
    const rateGroups: Record<string, string> = {};
    serviceBooking.quantities.forEach((q) => {
      quantities[q.optionId] = q.quantity;
      if (q.rateGroup) rateGroups[q.optionId] = q.rateGroup;
    });
    const addOnQuantities: Record<string, number> = {};
    serviceBooking.addOns.forEach((a) => { addOnQuantities[a.id] = a.qty; });
    return previewBookingPrice({
      service: detail, quantities, rateGroups, resourceKey: serviceBooking.resourceKey,
      bookingDate: serviceBooking.date, addOnQuantities,
    });
  }, [detail, serviceBooking]);
  const previewOk = preview && isPreviewOk(preview) ? preview : null;
  const groupLabel = (id?: string) => {
    const g = detail?.rate_groups?.find((x) => String(x.id) === id);
    return g ? labelOf(g.label, g.id, lang) : id;
  };

  const hasBooking = Boolean(product || serviceBooking);
  // The preview carries the season's English label; the service's own {en, es} label gives the active language.
  const seasonRaw = (detail?.pricing?.seasons as Array<{ id: string; label?: { en?: string | null; es?: string | null } }> | undefined)
    ?.find((s) => s.id === previewOk?.season?.id)?.label;
  const seasonLabel = previewOk?.season ? labelOf(seasonRaw, previewOk.season.label, lang) : '';

  return (
    <div
      className="flex-shrink-0"
      style={{ width: 260, borderLeft: '1px solid var(--line)', paddingLeft: 20, marginLeft: 4 }}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--ink-400)' }}>
        {t('booking.summary.title')}
      </div>

      {destinationLabel && <Row icon={<MapPin size={13} />} label={t('booking.summary.destination')} value={destinationLabel} />}
      {accessTypeLabel && <Row icon={<Ticket size={13} />} label={t('booking.summary.accessType')} value={accessTypeLabel} />}

      {product && (
        <Row
          icon={<Ticket size={13} />}
          label={t('booking.summary.product')}
          value={`${product.label}${product.price ? ` · $${product.price.toFixed(2)}` : ''}`}
        />
      )}

      {service && (
        <Row
          icon={<Ticket size={13} />}
          label={t('booking.summary.service')}
          value={lang === 'es' ? service.name_es || service.name || service.name_en : service.name_en || service.name || service.name_es}
        />
      )}

      {(invitation?.duration?.date || serviceBooking?.date) && (
        <Row
          icon={<Calendar size={13} />}
          label={t('booking.summary.date')}
          value={fmt.date((serviceBooking?.date || invitation?.duration?.date || '').slice(0, 10))}
        />
      )}

      {serviceBooking?.time && (
        <Row
          icon={<Clock size={13} />}
          label={t('booking.summary.time')}
          // A 'day' service has no meaningful time-of-day, whether its schedule entry came back
          // day-shaped (time === '00:00') or as a single slot the picker treated as the whole day
          // (see ServiceScheduleSlotPicker's isWholeDayBooking) — either way, show "Full day"
          // rather than a specific clock time that would contradict its own step's own copy.
          value={serviceBooking.time === '00:00' || isDayService(service) ? t('booking.summary.fullDay') : serviceBooking.time}
        />
      )}

      {guestCount > 0 && (
        <Row
          icon={<Users size={13} />}
          label={t('booking.summary.guests')}
          value={`${t('booking.summary.total', { n: guestCount })}${childrens > 0
            ? ` (${t(adults === 1 ? 'booking.summary.adultOne' : 'booking.summary.adultMany', { n: adults })}, ${t(childrens === 1 ? 'booking.summary.childOne' : 'booking.summary.childMany', { n: childrens })})`
            : ''}`}
        />
      )}

      {serviceBooking && serviceBooking.quantities.length > 0 && service?.booking_options && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--line-subtle)' }}>
          <div className="text-[11px] mb-1.5" style={{ color: 'var(--ink-400)' }}>
            {previewOk?.mode === 'unit' ? t('booking.summary.extraGuests') : previewOk?.mode === 'event' ? t('booking.summary.event') : t('booking.summary.perUnitPrices')}
          </div>
          {serviceBooking.quantities.map((q) => {
            const opt = service.booking_options!.find((o) => o.id === q.optionId);
            if (!opt) return null;
            const line = previewOk?.lines.find((l) => l.optionId === q.optionId);
            const price = line ? line.unitPrice : opt.price;
            return (
              <div key={q.optionId} className="flex justify-between gap-2 text-[12px] py-0.5" style={{ color: 'var(--ink-700)' }}>
                <span>
                  {labelOf({ en: opt.label_en, es: opt.label_es }, opt.id, lang)} × {q.quantity}
                  {q.rateGroup && (
                    <span className="block text-[11px]" style={{ color: 'var(--ink-400)' }}>{groupLabel(q.rateGroup)}</span>
                  )}
                </span>
                <span>
                  {previewOk?.mode === 'unit' ? '+' : ''}{fmt.money(price, opt.currency)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {previewOk?.unit && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--line-subtle)' }}>
          <Row
            icon={<Home size={13} />}
            label={`${t('booking.unit.title')} · ${t('booking.summary.guestsIncluded', { n: previewOk.unit.includedPax })}${previewOk.unit.maxPax ? `, ${t('booking.unit.max', { max: previewOk.unit.maxPax })}` : ''}`}
            value={`${previewOk.unit.resourceName || t('booking.unit.fallbackName')} · ${fmt.money(previewOk.unit.price, previewOk.currency)}`}
          />
        </div>
      )}

      {previewOk?.event && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--line-subtle)' }}>
          <Row
            icon={<Users size={13} />}
            label={`${t('booking.event.size')} ${previewOk.event.band.toUpperCase()} · ${previewOk.event.bandLabel}`}
            value={t('booking.summary.flat', { price: fmt.money(previewOk.event.price, previewOk.currency) })}
          />
        </div>
      )}

      {previewOk?.season && (
        <Row icon={<CalendarRange size={13} />} label={t('booking.summary.seasonPricing')} value={seasonLabel} />
      )}

      {serviceBooking && serviceBooking.addOns.length > 0 && service?.settings?.add_ons && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--line-subtle)' }}>
          <div className="text-[11px] mb-1.5" style={{ color: 'var(--ink-400)' }}>{t('booking.addOns')}</div>
          {serviceBooking.addOns.map((a) => {
            const addOn = service.settings!.add_ons!.find((x) => x.id === a.id);
            if (!addOn) return null;
            return (
              <div key={a.id} className="flex justify-between text-[12px] py-0.5" style={{ color: 'var(--ink-700)' }}>
                <span>{labelOf({ en: addOn.label_en, es: addOn.label_es }, addOn.id, lang)} × {a.qty}</span>
                <span>{fmt.money(addOn.price, addOn.currency || 'USD')}</span>
              </div>
            );
          })}
        </div>
      )}

      {previewOk && serviceBooking && (
        <div className="flex justify-between items-baseline mt-3 pt-3 text-[13px]" style={{ borderTop: '1px solid var(--line-subtle)', color: 'var(--ink-900)' }}>
          <span className="font-medium">{t('booking.summary.estimatedTotal')}</span>
          <span className="font-semibold">{fmt.money(previewOk.total, previewOk.currency)}</span>
        </div>
      )}

      {hasBooking && (
        <p className="text-[11px] mt-3" style={{ color: 'var(--ink-400)' }}>
          {t('booking.totalConfirmedByServer')}
        </p>
      )}

      {!hasBooking && !destinationLabel && (
        <p className="text-[12px]" style={{ color: 'var(--ink-400)' }}>{t('booking.summary.nothingSelected')}</p>
      )}
    </div>
  );
};
